import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  useCreateClassmedCheckoutSessionMutation,
  useGetIngredientsQuery,
  useUpdateClassmedFormulaMutation,
} from '../features/quizApiSlice';
import useFormulaEditor from './useFormulaEditor';
import { saveClassmedResult } from '../utils/classmed';
import { calculateClassmedNinetyDayPrice } from '../utils/classmed-pricing';
import { getIngredientMonthlyPrice } from '../utils/productSuggestions';

const CHECKOUT_DURATIONS = [30, 90];
const PRELOADED_CHECKOUT_TTL_MS = 20 * 60 * 1000;

const toCheckoutIngredients = (ingredients = []) =>
  ingredients.map((ingredient) => ({
    ingredientId: ingredient?.ingredient_id,
    quantity: Number(ingredient?.daily_dose),
  }));

const getFormulaPrices = (ingredients = []) =>
  ingredients.reduce((formulaPrices, ingredient) => ({
    ...formulaPrices,
    [ingredient?.ingredient]: getIngredientMonthlyPrice(ingredient, ingredient?.daily_dose),
  }), {});

const getFormulaSignature = (ingredients = []) =>
  ingredients
    .map((ingredient) => {
      const ingredientId = Number(ingredient?.ingredient_id);
      const quantity = Number(ingredient?.daily_dose);
      return `${ingredientId}:${Number.isFinite(quantity) ? quantity.toFixed(6) : ''}`;
    })
    .sort()
    .join('|');

const isPreloadedCheckoutFresh = (checkout) =>
  Boolean(checkout?.url && Date.now() - checkout.createdAt < PRELOADED_CHECKOUT_TTL_MS);

const useClassmedProduct = ({ result, classmedId, pharmacyId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(result?.ingredients || []);
  const [originalIngredients, setOriginalIngredients] = useState(result?.ingredients || []);
  const [hasFormulaChanged, setHasFormulaChanged] = useState(false);
  const [error, setError] = useState({});
  const [prices, setPrices] = useState({});
  const [pricesData, setPricesData] = useState({});
  const [selectedDuration, setSelectedDuration] = useState(90);
  const [addIngredientsModal, setAddIngredientsModal] = useState(false);
  const [preloadedCheckoutUrls, setPreloadedCheckoutUrls] = useState({});
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isFormulaSaving, setIsFormulaSaving] = useState(false);
  const preloadRequestsRef = useRef({});
  const { data: allIngredients, isLoading: isAllIngredientsLoading } = useGetIngredientsQuery('fr');
  const [createCheckoutSession] = useCreateClassmedCheckoutSessionMutation();
  const [updateClassmedFormula] = useUpdateClassmedFormulaMutation();

  useEffect(() => {
    if (!result?.ingredients?.length) return;
    const initialPrices = getFormulaPrices(result.ingredients);

    setData(result.ingredients);
    setOriginalIngredients(result.ingredients);
    setPrices(initialPrices);
    setPricesData(initialPrices);
    setHasFormulaChanged(false);
  }, [result?.ingredients, result?.quiz_id]);

  useEffect(() => {
    const handlePageShow = () => {
      setIsCheckoutLoading(false);
      setIsFormulaSaving(false);
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const quote = useMemo(() => {
    const monthlyTotal = Object.values(prices).reduce(
      (total, ingredientPrice) => total + Number(ingredientPrice || 0),
      0,
    );
    const monthlyPrice = Math.round(monthlyTotal);

    return {
      currency: 'EUR',
      prices: {
        30: monthlyPrice,
        90: calculateClassmedNinetyDayPrice(monthlyPrice),
      },
    };
  }, [prices]);

  const handleQuantityPriceChange = useCallback((ingredient, quantity) => {
    setPrices((currentPrices) => ({
      ...currentPrices,
      [ingredient?.ingredient]: getIngredientMonthlyPrice(ingredient, quantity),
    }));
  }, []);

  const {
    addIngredient,
    onAddQuantity,
    onInputChange,
    onRemoveQuantity,
    removeIngredient,
    removeIngredientFromBin,
    resetFormula,
  } = useFormulaEditor({
    setData,
    originalIngredients,
    setHasFormulaChanged,
    setError,
    setPrices,
    pricesData,
    allIngredients,
    onQuantityPriceChange: handleQuantityPriceChange,
  });

  const hasErrors = useMemo(() => Object.values(error).some((hasError) => hasError === true), [error]);

  const buildCheckoutRequest = useCallback(
    (duration, formulaData = data) => {
      if (!classmedId || !pharmacyId || !result?.quiz_id || !CHECKOUT_DURATIONS.includes(Number(duration))) {
        return null;
      }

      const formulaSignature = getFormulaSignature(formulaData);
      if (!formulaSignature) return null;

      return {
        cacheKey: [classmedId, pharmacyId, result.quiz_id, duration, formulaSignature].join('::'),
        payload: {
          classmedId,
          pharmacyId,
          quizId: result.quiz_id,
          duration,
          ingredients: toCheckoutIngredients(formulaData),
        },
      };
    },
    [classmedId, data, pharmacyId, result?.quiz_id],
  );

  const createCheckoutUrl = useCallback(
    async (checkoutRequest, { force = false } = {}) => {
      if (!checkoutRequest) throw new Error(t('Classmed_CheckoutError'));

      const { cacheKey, payload } = checkoutRequest;
      const cachedCheckout = preloadedCheckoutUrls[cacheKey];
      if (!force && isPreloadedCheckoutFresh(cachedCheckout)) return cachedCheckout.url;

      if (preloadRequestsRef.current[cacheKey]) {
        return preloadRequestsRef.current[cacheKey];
      }

      const checkoutPromise = createCheckoutSession(payload)
        .unwrap()
        .then((response) => {
          const checkoutUrl = response?.data?.url;
          if (!checkoutUrl) throw new Error(t('Classmed_CheckoutError'));

          setPreloadedCheckoutUrls((currentUrls) => ({
            ...currentUrls,
            [cacheKey]: {
              url: checkoutUrl,
              createdAt: Date.now(),
            },
          }));

          return checkoutUrl;
        })
        .finally(() => {
          delete preloadRequestsRef.current[cacheKey];
        });

      preloadRequestsRef.current[cacheKey] = checkoutPromise;
      return checkoutPromise;
    },
    [createCheckoutSession, preloadedCheckoutUrls, t],
  );

  const preloadCheckoutSessions = useCallback(
    (formulaData = data, options = {}) => {
      if (!formulaData.length || hasErrors) return Promise.resolve([]);

      const preloadPromises = CHECKOUT_DURATIONS.map((duration) =>
        createCheckoutUrl(buildCheckoutRequest(duration, formulaData), options).catch(() => null),
      );

      return Promise.all(preloadPromises);
    },
    [buildCheckoutRequest, createCheckoutUrl, data, hasErrors],
  );

  useEffect(() => {
    if (hasFormulaChanged || !data.length || hasErrors) return;
    preloadCheckoutSessions();
  }, [data, hasErrors, hasFormulaChanged, preloadCheckoutSessions]);

  const saveFormula = useCallback(
    async (formulaData = data) => {
      setIsFormulaSaving(true);

      try {
        await updateClassmedFormula({
          classmedId,
          quizId: result?.quiz_id,
          ingredients: toCheckoutIngredients(formulaData),
        }).unwrap();

        setOriginalIngredients(formulaData);
        setPricesData(getFormulaPrices(formulaData));
        setHasFormulaChanged(false);
        saveClassmedResult(
          {
            ...result,
            ingredients: formulaData,
          },
          classmedId,
          'fr',
        );
      } finally {
        setIsFormulaSaving(false);
      }
    },
    [classmedId, data, result, updateClassmedFormula],
  );

  const handleFormulaEditComplete = useCallback(async () => {
    await preloadCheckoutSessions(data, { force: hasFormulaChanged });
  }, [data, hasFormulaChanged, preloadCheckoutSessions]);

  const handleCheckout = useCallback(async () => {
    if (!quote || hasErrors || isCheckoutLoading || isFormulaSaving) return;

    setIsCheckoutLoading(true);

    try {
      const checkoutUrlPromise = createCheckoutUrl(
        buildCheckoutRequest(selectedDuration, data),
      );
      const formulaSavePromise = hasFormulaChanged ? saveFormula(data) : Promise.resolve();
      const [checkoutUrl] = await Promise.all([checkoutUrlPromise, formulaSavePromise]);

      window.location.href = checkoutUrl;
    } catch (error) {
      setIsCheckoutLoading(false);
      toast.error(error?.data?.message || t('Classmed_CheckoutError'));
    }
  }, [
    buildCheckoutRequest,
    createCheckoutUrl,
    data,
    hasErrors,
    hasFormulaChanged,
    isCheckoutLoading,
    isFormulaSaving,
    quote,
    saveFormula,
    selectedDuration,
    t,
  ]);

  return {
    addIngredient,
    addIngredientsModal,
    allIngredients,
    data,
    error,
    handleCheckout,
    handleFormulaEditComplete,
    hasFormulaChanged,
    isAllIngredientsLoading,
    isCheckoutLoading,
    isFormulaSaving,
    onAddQuantity,
    onInputChange,
    onRemoveQuantity,
    originalIngredients,
    prices,
    pricesData,
    quote,
    removeIngredient,
    removeIngredientFromBin,
    resetFormula,
    selectedDuration,
    setAddIngredientsModal,
    setData,
    setError,
    setHasFormulaChanged,
    setPrices,
    setSelectedDuration,
  };
};

export default useClassmedProduct;
